import {useNavigate, useSearchParams} from "react-router-dom";
import {resolveTab} from "src/utils/tab.util";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import SettingsEntityHeader from "../../../layout/SettingsEntityHeader";
import DetailsLoading from "crqlar-react-common/dist/components/DetailsLoading";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import {OhipService} from "crqlar-react-common/dist/services/integrations/ohip/ohip.service";
import {Box, Divider, Stack, Tab} from "@mui/material";
import {TabContext} from "@mui/lab";
import Typography from "@mui/material/Typography";
import {OhipConfig} from "crqlar-react-common/dist/models/integrations/ohip/ohip.config";
import {SectorService} from "crqlar-react-common/dist/services/sector/sector.service";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import OhipConfigForm from "./OhipConfigForm";
import OhipTasks from "./OhipTasks";
import Logs from "./Logs";
import EmptyState from "crqlar-react-common/dist/components/EmptyState";
import Button from "@mui/material/Button";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";
import ScrollableTabList from "crqlar-react-common/dist/components/tabs/ScrollableTabList";
import {getDefaultAgeCategoryIds} from "src/utils/integrationDefaults.util";


const OhipConfigDetails = () => {

    useTrackScreenVisit('system ohip config details')

    //region State

    const {t} = useTranslation()
    const navigate = useNavigate()

    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = resolveTab(searchParams.get('tab'), ['config', 'tasks', 'logs'], 'config');
    const setActiveTab = (newTab: string) => {
        setSearchParams({tab: newTab});
    };

    const ohipConfigData = useEntity<OhipConfig>({
        get: async () => {
            return await OhipService.find()
        },
        create: OhipService.create,
        update: OhipService.update,
        delete: (id) => OhipService.delete({id: [id]}),
        isValid: (ohipConfig) => {
            return !!ohipConfig.name
        },
        cleanupBeforeSave: (ohipConfig) => {
            return {
                ...ohipConfig,
                name: ohipConfig.name?.trim()
            }
        }
    })

    const ohipConfig = ohipConfigData.item

    const handleCreate = async () => {
        const [sectors, defaultAgeCategoryIds] = await Promise.all([
            SectorService.getAllSectors({limit: {offset: 0, limit: 1000}}),
            getDefaultAgeCategoryIds()
        ])

        ohipConfigData.startCreate({
            pollerConfig: {
                fullSync: {
                    profileDaysPast: 1000,
                    reservationDaysPast: 365,
                    staleTimeoutMinutes: 5 * 60,
                },
                quickSync: {
                    staleTimeoutMinutes: 10,
                    syncPeriodMinutes: 5
                }
            },
            dataProcessingRules: {
                accommodationSectorId: sectors.content?.find(s => s.sectorType === SectorType.ACCOMMODATION)?.id,
                ...defaultAgeCategoryIds,
                reservationStatusToKeep: ["1"],
                reservationStatesToKeep: ["0", "1", "2"],
                autoCreateResourceIfNotFound: true,
                autoCreateServiceForRatePlanIfNotFound: true
            }
        } as OhipConfig)
    }

    //endregion State

    const headerSection = (
        <SettingsEntityHeader
            title={ohipConfig.name}
            data={ohipConfigData}
            back={() => navigate('/system')}
        />
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete integration')}
                description={t('This permanently deletes the integration configuration and stops its syncing immediately. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={ohipConfigData.saving}
                onDelete={() => ohipConfigData.removeNow()}
                reloadOnDelete
            />

            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {ohipConfig.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    const content = (
        <>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {
                    ohipConfigData.isMissing &&
                    <EmptyState
                        icon={<ElectricalServicesIcon/>}
                        title={t('No integration configured')}
                        description={t('Create the configuration to connect this integration.')}
                    >
                        <Button variant={'contained'} onClick={handleCreate}>
                            {t('Create')}
                        </Button>
                    </EmptyState>
                }

                {
                    !!ohipConfig.id &&
                    <TabContext value={activeTab}>
                        <ScrollableTabList onChange={(e, v) => setActiveTab(v)}>
                            <Tab label={t('Configuration')} value={'config'}/>
                            <Tab label={t('Tasks')} value={'tasks'}/>
                            <Tab label={t('Stats and Logs')} value={'logs'}/>
                        </ScrollableTabList>
                    </TabContext>
                }

                {
                    !ohipConfigData.isMissing && activeTab === 'config' && (
                        <>
                            <OhipConfigForm ohipConfigData={ohipConfigData}/>

                            {
                                !!ohipConfig.id &&
                                <>
                                    <Divider sx={{mt: 4}}/>
                                    {dangerZoneSection}
                                </>
                            }
                        </>
                    )
                }

                {
                    activeTab === 'tasks' && (
                        <OhipTasks configData={ohipConfigData}/>
                    )
                }

                {
                    activeTab === 'logs' && (
                        <Logs configData={ohipConfigData}/>
                    )
                }
            </Stack>
        </>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {ohipConfigData.loading ? <>{headerSection}<DetailsLoading/></> : content}
        </Box>
    );
}

export default OhipConfigDetails;
